/**
 * Common Auth Utility Functions
 * Centralized utilities for common authentication operations
 */

import { COMMON_AUTH_CONFIG } from '../const/commonAuthConfig';

export const TABLE_PREFERENCE_STORAGE_PREFIXES = {
  columnOrder: 'tableColumnOrder_',
  hiddenColumns: 'tableHiddenColumns_',
};

/**
 * Validates if a redirect URL is safe to use (prevents open redirect attacks)
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if the URL is safe to redirect to
 */
const isValidRedirectUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const currentOrigin = window.location.origin;

    // Allow only same-origin redirects
    if (urlObj.origin === currentOrigin) {
      return true;
    }
  } catch (_error) {
    // If URL parsing fails, consider it invalid
    return false;
  }
};

/**
 * Centralized redirect function for common auth logout
 * Makes it easy to change redirect behavior in one place
 */
export const redirectToLogout = (
  delay = COMMON_AUTH_CONFIG.LOGOUT_REDIRECT_DELAY,
  keepRedirectParams = true
) => {
  setTimeout(() => {
    // Check if loginWithNormalAuth is present in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoginWithNormalAuth = urlParams.has('loginWithNormalAuth');

    // Define public routes that should not include redirect parameter
    const publicRoutePaths = [
      '/login',
      '/account',
      '/org',
      '/clear-client-storage',
    ];

    // Check if current path matches any public route (including dynamic segments like /login/:accountId)
    const currentPath = window.location.pathname;
    const isPublicRoute = publicRoutePaths.some((route) => {
      if (route === '/login') {
        // Handle both /login and /login/:accountId
        return currentPath === '/login' || currentPath.startsWith('/login/');
      }
      return currentPath === route;
    });

    if (hasLoginWithNormalAuth || isPublicRoute) {
      // If loginWithNormalAuth is present or current page is a public route, redirect without redirect parameter
      window.location.href = COMMON_AUTH_CONFIG.LOGOUT_REDIRECT_URL;
    } else {
      // Validate the home URL before using it as a redirect parameter
      //keepRedirectParams is used to determine if we should keep the full current URL or just redirect to home
      let currentUrl;
      if (keepRedirectParams) {
        currentUrl = window.location.href;
      } else {
        currentUrl = window.location.origin + '/home?value=0';
      }

      if (isValidRedirectUrl(currentUrl)) {
        // Safe to include redirect parameter
        window.location.href = `${COMMON_AUTH_CONFIG.LOGOUT_REDIRECT_URL}?redirect=${encodeURIComponent(currentUrl)}`;
      } else {
        // URL validation failed, redirect without redirect parameter for security
        window.location.href = COMMON_AUTH_CONFIG.LOGOUT_REDIRECT_URL;
      }
    }
  }, delay);
};

/**
 * Utility function to clear localStorage while preserving important values
 * This ensures consistent behavior across all logout functions
 * @param {string[]} additionalKeysToKeep - Additional keys to preserve beyond default ones
 * @param {boolean} clearAll - If true, clears everything without preserving default keys
 * @returns {object} Object containing the preserved values
 */
export const clearStorageWithPreservation = (
  additionalKeysToKeep = [],
  clearAll = false
) => {
  const defaultKeysToKeep = clearAll
    ? []
    : ['accountId', 'forceNormalAuth', 'ACK_SEEN_USERS'];
  const keysToKeep = [...defaultKeysToKeep, ...additionalKeysToKeep];
  const valuesToKeep = {};
  const prefixesToKeep = clearAll
    ? []
    : Object.values(TABLE_PREFERENCE_STORAGE_PREFIXES);
  const preserveStorageValue = (key) => {
    if (Object.prototype.hasOwnProperty.call(valuesToKeep, key)) {
      return;
    }

    const value = localStorage.getItem(key);
    if (value && value !== 'null' && value !== 'undefined') {
      valuesToKeep[key] = value;
    }
  };

  // Build one valuesToKeep object from exact keys first.
  keysToKeep.forEach(preserveStorageValue);

  // Then add any keys that match the allowed prefixes.
  if (prefixesToKeep.length > 0) {
    Object.keys(localStorage).forEach((key) => {
      if (prefixesToKeep.some((prefix) => key.startsWith(prefix))) {
        preserveStorageValue(key);
      }
    });
  }

  // Clear all storage
  localStorage.clear();
  sessionStorage.clear();

  // Restore preserved values
  Object.entries(valuesToKeep).forEach(([key, value]) => {
    localStorage.setItem(key, value);
  });

  return valuesToKeep;
};

/**
 * Clear all common auth related data from localStorage
 * Keeps accountId and forceNormalAuth if they exist (as per business requirement)
 */
export const clearCommonAuthData = () => {
  clearStorageWithPreservation();
};

/**
 * Clear all storage completely without preserving any values
 * Use this for complete logout/cache clearing scenarios
 */
export const clearAllStorage = () => {
  clearStorageWithPreservation([], true);
};
